import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { StorageService } from './storage.service';

type SidebarMode = 'expanded' | 'collapsed';
type MonitoringRailMode = 'open' | 'collapsed';
type StageViewMode = 'grid' | 'list';
type SimulationsViewMode = 'grid' | 'list';

/**
 * Typed, namespaced ('ui.*') accessors over StorageService for every f-user
 * layout/preference key. Each pref is a WritableSignal hydrated once at
 * construction time (lazy, synchronous — no async gap, so callers reading
 * from initConfig.ts-style APP_INITIALIZER factories still work) with a
 * typed default, guarded against absent/malformed JSON.
 *
 * This service is the single source of truth for f-user UI prefs from this
 * point forward — see docs/superpowers/specs/2026-09-04-monitoring-ux-revamp-design.md
 * §9.1 for the full key-rename rationale (no back-compat shim; old bare key
 * names like 'theme'/'navOpen' are never read again).
 */
@Injectable({ providedIn: 'root' })
export class UiPrefsService {
  private readonly storage = inject(StorageService);

  private hydrate<T>(key: string, defaultValue: T): WritableSignal<T> {
    const raw = this.storage.getItem(key);
    if (raw === null) return signal(defaultValue);
    try {
      return signal(JSON.parse(raw) as T);
    } catch {
      return signal(defaultValue);
    }
  }

  private persist<T>(key: string, sig: WritableSignal<T>, value: T): void {
    sig.set(value);
    this.storage.setItem(key, JSON.stringify(value));
  }

  readonly sidebarMode = this.hydrate<SidebarMode>('ui.sidebar.mode', 'expanded');
  setSidebarMode(value: SidebarMode): void { this.persist('ui.sidebar.mode', this.sidebarMode, value); }

  readonly sidebarWidth = this.hydrate<number>('ui.sidebar.width', 320);
  setSidebarWidth(value: number): void { this.persist('ui.sidebar.width', this.sidebarWidth, value); }

  readonly monitoringRailMode = this.hydrate<MonitoringRailMode>('ui.monitoringRail.mode', 'collapsed');
  setMonitoringRailMode(value: MonitoringRailMode): void { this.persist('ui.monitoringRail.mode', this.monitoringRailMode, value); }

  readonly monitoringRailWidth = this.hydrate<number>('ui.monitoringRail.width', 300);
  setMonitoringRailWidth(value: number): void { this.persist('ui.monitoringRail.width', this.monitoringRailWidth, value); }

  readonly monitoringRailPinned = this.hydrate<boolean>('ui.monitoringRail.pinned', false);
  setMonitoringRailPinned(value: boolean): void { this.persist('ui.monitoringRail.pinned', this.monitoringRailPinned, value); }

  readonly stageViewMode = this.hydrate<StageViewMode>('ui.stage.viewMode', 'grid');
  setStageViewMode(value: StageViewMode): void { this.persist('ui.stage.viewMode', this.stageViewMode, value); }

  readonly simulationsViewMode = this.hydrate<SimulationsViewMode>('ui.simulations.viewMode', 'grid');
  setSimulationsViewMode(value: SimulationsViewMode): void { this.persist('ui.simulations.viewMode', this.simulationsViewMode, value); }

  // --- migrated legacy prefs (renamed from bare keys, no back-compat shim) ---

  readonly navOpen = this.hydrate<boolean>('ui.nav.open', true);
  setNavOpen(value: boolean): void { this.persist('ui.nav.open', this.navOpen, value); }

  readonly themeMode = this.hydrate<'light' | 'dark'>('ui.theme.mode', 'dark');
  setThemeMode(value: 'light' | 'dark'): void { this.persist('ui.theme.mode', this.themeMode, value); }

  readonly locale = this.hydrate<'es_ES' | 'en_US'>('ui.locale', 'es_ES');
  setLocale(value: 'es_ES' | 'en_US'): void { this.persist('ui.locale', this.locale, value); }

  readonly textSize = this.hydrate<'small' | 'default' | 'large'>('ui.textSize', 'default');
  setTextSize(value: 'small' | 'default' | 'large'): void { this.persist('ui.textSize', this.textSize, value); }
}
